/**
 * Field configurations for Project Objectives form
 */

export const PO_PICKLIST_LABELS = {
  COUNTRY: "Country__c",
  LANGUAGE: "Language__c",
  WORK_TYPE: "Work_Type__c",
  DIALECT: "Dialect_Country__c",
  LANGUAGE_SKILL_LEVEL: "Language_Skill_Level__c",
  FLUENCY_TYPE: "Fluency_Type__c",
  PRODUCTIVITY_TARGET_TYPE: "Productivity_Target_Type__c",
  DEGREE_REQUIREMENT: "Degree_Requirement__c",
  CURRENTLY_RESISDING_US_STATE: "Currently_Residing_US_State__c",
};

export const objectiveSections = {
  information: {
    title: "Information",
    key: "information",
    fields: [
      {
        cols: 2,
        fields: [
          {
            label: "Append to Objective Name",
            path: "appendToObjectiveName",
            type: "text",
          },
        ],
      },
      {
        label: "Project",
        path: "project",
        type: "text",
        required: true,
        readOnly: true,
        info: "Search and select project",
      },
      {
        cols: 2,
        fields: [
          { label: "Date Start", path: "dateStart", type: "date" },
          { label: "Date End", path: "dateEnd", type: "date" },
        ],
      },
      {
        label: "Selection Criteria",
        path: "selectionCriteria",
        type: "textarea",
        info: "Criteria for selecting contributors",
      },
      {
        label: "Project Objective Description",
        path: "projectObjectiveDescription",
        type: "textarea",
      },
    ],
  },

  requirements: {
    title: "Requirements",
    key: "requirements",
    fields: [
      {
        cols: 2,
        fields: [
          {
            label: "Country",
            path: "country",
            type: "select",
            picklistLabel: PO_PICKLIST_LABELS.COUNTRY,
            info: "Either country or language must be selected",
          },
          {
            label: "Language",
            path: "language",
            type: "select",
            picklistLabel: PO_PICKLIST_LABELS.LANGUAGE,
          },
        ],
      },
      {
        cols: 2,
        fields: [
          {
            label: "Exclude US States",
            path: "excludeStates",
            type: "checkbox",
          },
          {
            label: "Currently Residing US State",
            path: "currentlyResidingUsState",
            type: "multi-select",
            picklistLabel: PO_PICKLIST_LABELS.CURRENTLY_RESISDING_US_STATE,
            required: true,
          },
        ],
      },
      {
        label: "Work Type",
        path: "workType",
        type: "select",
        picklistLabel: PO_PICKLIST_LABELS.WORK_TYPE,
        required: true,
      },
      {
        cols: 2,
        fields: [
          {
            label: "Dialect (Country)",
            path: "dialect",
            type: "select",
            picklistLabel: PO_PICKLIST_LABELS.DIALECT,
          },
          {
            label: "Degree Requirement",
            path: "degreeRequirement",
            type: "select",
            picklistLabel: PO_PICKLIST_LABELS.DEGREE_REQUIREMENT,
          },
        ],
      },
      {
        cols: 2,
        fields: [
          {
            label: "Language Skill Level",
            path: "languageSkillLevel",
            type: "select",
            picklistLabel: PO_PICKLIST_LABELS.LANGUAGE_SKILL_LEVEL,
          },
          {
            label: "Fluency Type",
            path: "fluencyType",
            type: "select",
            picklistLabel: PO_PICKLIST_LABELS.FLUENCY_TYPE,
          },
        ],
      },
      {
        cols: 2,
        fields: [
          {
            label: "Include in Country List",
            path: "includeInCountryList",
            type: "checkbox",
          },
          {
            label: "List of Countries",
            path: "listOfCountries",
            type: "multi-select",
            picklistLabel: PO_PICKLIST_LABELS.COUNTRY,
          },
        ],
      },
      {
        label: "Additional Manual Match Required",
        path: "additionalManualMatchRequired",
        type: "checkbox",
      },
    ],
  },

  productivity: {
    title: "Productivity & Quality",
    key: "productivity",
    fields: [
      {
        label: "Productivity Target Type",
        path: "productivityTargetType",
        type: "select",
        picklistLabel: PO_PICKLIST_LABELS.PRODUCTIVITY_TARGET_TYPE,
        required: true,
      },
      {
        cols: 2,
        fields: [
          {
            label: "Target Production Hours",
            path: "targetProductionHours",
            type: "number",
          },
          { label: "Unit Pay Rate", path: "unitPayRate", type: "number" },
        ],
      },
      { label: "Main Quality Metric", path: "mainQualityMetric", type: "text" },
      {
        cols: 2,
        fields: [
          {
            label: "Contributor Quality Flag",
            path: "contributorQualityFlag",
            type: "text",
          },
          {
            label: "Contributor Productivity Flag",
            path: "contributorProductivityFlag",
            type: "text",
          },
        ],
      },
      {
        label: "Contributor Overproductivity Flag",
        path: "contributorOverproductivityFlag",
        type: "text",
      },
      {
        cols: 2,
        fields: [
          {
            label: "Production Payment Threshold",
            path: "productionPaymentThreshold",
            type: "number",
          },
          {
            label: "Quality Payment Threshold",
            path: "qualityPaymentThreshold",
            type: "number",
          },
        ],
      },
    ],
  },

  funnel: {
    title: "Funnel Configuration",
    key: "funnel",
    fields: [
      {
        cols: 2,
        fields: [
          { label: "Funnel A (%)", path: "funnelAPercentage", type: "number" },
          { label: "Funnel B (%)", path: "funnelBPercentage", type: "number" },
        ],
      },
      {
        cols: 2,
        fields: [
          { label: "Funnel C (%)", path: "funnelCPercentage", type: "number" },
          { label: "Funnel D (%)", path: "funnelDPercentage", type: "number" },
        ],
      },
      { label: "Funnel E (%)", path: "funnelEPercentage", type: "number" },
    ],
  },

  rates: {
    title: "Rates & Payment",
    key: "rates",
    fields: [
      {
        cols: 2,
        fields: [
          { label: "Minimum Rate", path: "minimumRate", type: "number" },
          { label: "Maximum Rate", path: "maximumRate", type: "number" },
        ],
      },
      {
        cols: 2,
        fields: [
          { label: "Pay Interval", path: "payInterval", type: "text" },
          { label: "Hide Payrate", path: "hidePayRate", type: "checkbox" },
        ],
      },
      {
        cols: 2,
        fields: [
          { label: "Unit Pay Rate", path: "unitPayRate", type: "number" },
          {
            label: "Project Objective Pay Rate",
            path: "projectObjectivePayRate",
            type: "number",
          },
        ],
      },
    ],
  },

  actionRules: {
    title: "Action Rules & Flagging",
    key: "actionRules",
    fields: [
      {
        cols: 2,
        fields: [
          {
            label: "Days Between Reminder Emails",
            path: "daysBetweenReminderEmails",
            type: "number",
            default: 5,
          },
          {
            label: "Email Content Complete",
            path: "emailContentComplete",
            type: "checkbox",
          },
        ],
      },
    ],
  },

  system: {
    title: "System Information",
    key: "system",
    fields: [
      {
        cols: 2,
        fields: [
          {
            label: "Created By",
            path: "createdBy",
            type: "text",
            readOnly: true,
            info: "Calculated upon save",
          },
          {
            label: "Last Modified By",
            path: "lastModifiedBy",
            type: "text",
            readOnly: true,
            info: "Calculated upon save",
          },
        ],
      },
    ],
  },
};

export const initialFormState = {
  contributorFacingName: "",
  objectiveName: "",
  appendToObjectiveName: "",
  project: "",
  projectId: "",
  dateStart: "",
  dateEnd: "",
  selectionCriteria: "",
  projectObjectiveDescription: "",
  country: "",
  excludeStates: false,
  currentlyResidingUsState: [],
  language: "",
  workType: "",
  dialect: "",
  degreeRequirement: "",
  languageSkillLevel: "",
  fluencyType: "",
  additionalManualMatchRequired: false,
  capDailyHoursSubmitted: false,
  includeInCountryList: false,
  listOfCountries: [],
  productivityTargetType: "Hours",
  targetProductionHours: "",
  mainQualityMetric: "",
  contributorQualityFlag: "",
  contributorProductivityFlag: "",
  contributorOverproductivityFlag: "",
  funnelAPercentage: "100",
  funnelBPercentage: "0",
  funnelCPercentage: "0",
  funnelDPercentage: "0",
  funnelEPercentage: "0",
  minimumRate: "",
  maximumRate: "",
  payInterval: "",
  unitPayRate: "",
  daysBetweenReminderEmails: "5",
  emailContentComplete: false,
  createdBy: "",
  lastModifiedBy: "",
  productionPaymentThreshold: "",
  qualityPaymentThreshold: "",
};
